import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/* The Letters piece, running on the project page rather than being shown as a
   picture of itself.
 *
 * Same construction as the wordmark in the header and the standalone build in
 * Creative/Letters: every letter holds its own pool of photographed forms,
 * normalised to one height, butted together with a negative margin.
 *
 * Two deliberate differences from the standalone version. Nothing cycles on a
 * timer, so the piece only changes when someone changes it. And there is no
 * bounce on hover. The grain is in the image files themselves, so it stays.
 */

const ORDER = ['L', 'U', 'K', 'E', 'A', 'B', 'C'];

export default function LettersPlate() {
  const wrapRef = useRef(null);
  const logoRef = useRef(null);

  const pools = useMemo(() => {
    const sets = (typeof window !== 'undefined' && window.LETTERS) || {};
    return ORDER.map((ch) => (sets[ch] || []).map((f) => `letters/${ch}/${f}`));
  }, []);

  const pick = useCallback((pool, avoid) => {
    if (!pool.length) return null;
    let candidate = pool[Math.floor(Math.random() * pool.length)];
    let guard = 0;
    while (pool.length > 1 && candidate === avoid && guard++ < 8) {
      candidate = pool[Math.floor(Math.random() * pool.length)];
    }
    return candidate;
  }, []);

  const [srcs, setSrcs] = useState(() => pools.map((pool) => pick(pool)));

  // the wordmark is wider than the column at this size, so scale it to fit
  const fit = useCallback(() => {
    const logo = logoRef.current;
    const wrap = wrapRef.current;
    if (!logo || !wrap) return;
    logo.style.transform = 'scale(1)';
    const natural = logo.scrollWidth;
    const available = wrap.clientWidth;
    if (natural > available && natural > 0) {
      logo.style.transform = `scale(${available / natural})`;
    }
  }, []);

  useEffect(() => {
    // preload every form so a swap never flashes
    pools.forEach((pool) => pool.forEach((src) => { const i = new Image(); i.src = src; }));
  }, [pools]);

  useEffect(() => {
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [fit, srcs]);

  const shuffle = useCallback(() => {
    setSrcs((prev) => pools.map((pool, i) => pick(pool, prev[i])));
  }, [pools, pick]);

  // step one letter through its own pool, forwards or back
  const step = useCallback((i, delta) => {
    setSrcs((prev) => {
      const pool = pools[i];
      if (pool.length < 2) return prev;
      const next = prev.slice();
      const at = pool.indexOf(prev[i]);
      next[i] = pool[((at + delta) % pool.length + pool.length) % pool.length];
      return next;
    });
  }, [pools]);

  // space shuffles the whole wordmark. Nothing else on the page uses it, and
  // it would otherwise scroll.
  useEffect(() => {
    function onKey(e) {
      if (e.code !== 'Space') return;
      const el = e.target;
      const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      if (typing) return;
      e.preventDefault();
      shuffle();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shuffle]);

  return (
    <div className="main-image letters-plate-wrap" ref={wrapRef}>
      <div className="letters-plate" ref={logoRef} aria-label="Lukeabc">
        {ORDER.map((ch, i) => (
          <div
            key={ch}
            className={'letters-tile' + (srcs[i] ? '' : ' empty')}
            onClick={() => step(i, 1)}
            onContextMenu={(e) => { e.preventDefault(); step(i, -1); }}
          >
            {srcs[i]
              ? <img src={srcs[i]} alt="" draggable="false" onLoad={fit} />
              : ch}
          </div>
        ))}
      </div>
      <div className="caption-text letters-plate-hint">
        Click a letter to change it. Space shuffles all seven.
      </div>
    </div>
  );
}
